import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import {
  type ActionFunctionArgs,
  json,
  type MetaFunction,
  redirect,
} from "@remix-run/node";
import { Link, useFetcher } from "@remix-run/react";
import { AuthenticityTokenInput } from "remix-utils/csrf/react";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { Resource } from "sst";
import { bus } from "sst/aws/bus";
import { z } from "zod";

import { prepareVerification } from "./verify.server";
import { Events } from "~/_events/index.server";
import { GeneralErrorBoundary } from "~/components/error-boundary";
import { ErrorList, Field } from "~/components/forms";
import { StatusButton } from "~/components/ui/status-button";
import { validateCSRF } from "~/lib/csrf.server";
import { checkHoneypot } from "~/lib/honeypot.server";
import { EmailSchema, UsernameSchema } from "~/lib/user-validation";
import { AccountsService } from "~/models/accounts/accountsService.server";

const ForgotPasswordSchema = z.object({
  usernameOrEmail: z.union([EmailSchema, UsernameSchema]),
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  await validateCSRF(formData, request.headers);
  checkHoneypot(formData);
  const submission = await parseWithZod(formData, {
    schema: ForgotPasswordSchema.superRefine(async (data, ctx) => {
      const { usernameOrEmail } = data;

      const {
        data: [user],
      } = await AccountsService.entities.users
        .find({})
        .where(
          ({ email, username }, { eq }) =>
            `${eq(email, usernameOrEmail)} OR ${eq(username, usernameOrEmail)}`,
        )
        .go();

      if (!user) {
        ctx.addIssue({
          path: ["usernameOrEmail"],
          code: z.ZodIssueCode.custom,
          message: "No user exists with this username or email",
        });

        return;
      }
    }),
    async: true,
  });
  if (submission.status !== "success") {
    return json(
      { result: submission.reply() },
      { status: submission.status === "error" ? 400 : 200 },
    );
  }
  const { usernameOrEmail } = submission.value;

  const {
    data: [user],
  } = await AccountsService.entities.users
    .find({})
    .where(
      ({ email, username }, { eq }) =>
        `${eq(email, usernameOrEmail)} OR ${eq(username, usernameOrEmail)}`,
    )
    .go();

  const { verifyUrl, redirectTo, otp } = await prepareVerification({
    period: 10 * 60,
    request,
    type: "reset-password",
    target: usernameOrEmail,
  });

  if (user) {
    try {
      await bus.publish(Resource.EventBus, Events.AuthForgotPassword, {
        email: user.email,
        verifyUrl: verifyUrl.toString(),
        otp,
      });

      return redirect(redirectTo.toString());
    } catch (e) {
      console.error("Event publish error", e);

      return json(
        {
          result: submission.reply({
            formErrors: ["Unable to send reset email"],
          }),
        },
        { status: 500 },
      );
    }
  }

  return json({ result: submission.reply() }, { status: 500 });
}

export const meta: MetaFunction = () => {
  return [{ title: "Password Recovery for CandidateStream" }];
};

export default function ForgotPasswordRoute() {
  const forgotPassword = useFetcher<typeof action>();

  const [form, fields] = useForm({
    id: "forgot-password-form",
    constraint: getZodConstraint(ForgotPasswordSchema),
    lastResult: forgotPassword.data?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: ForgotPasswordSchema });
    },
    shouldRevalidate: "onBlur",
  });

  return (
    <div className="container pb-32 pt-20">
      <div className="flex flex-col justify-center">
        <div className="text-center">
          <h1 className="text-h1">Forgot Password</h1>
          <p className="mt-3 text-body-md text-muted-foreground">
            No worries, we'll send you reset instructions.
          </p>
        </div>
        <div className="mx-auto mt-16 min-w-[368px] max-w-sm">
          <forgotPassword.Form method="POST" {...getFormProps(form)}>
            <AuthenticityTokenInput />
            <HoneypotInputs />
            <div>
              <Field
                labelProps={{
                  htmlFor: fields.usernameOrEmail.id,
                  children: "Username or Email",
                }}
                inputProps={{
                  autoFocus: true,
                  ...getInputProps(fields.usernameOrEmail, { type: "text" }),
                }}
                errors={fields.usernameOrEmail.errors}
              />
            </div>
            <ErrorList errors={form.errors} id={form.errorId} />

            <div className="mt-6">
              <StatusButton
                className="w-full"
                status={
                  forgotPassword.state === "submitting"
                    ? "pending"
                    : form.status ?? "idle"
                }
                type="submit"
                disabled={forgotPassword.state !== "idle"}
              >
                Recover password
              </StatusButton>
            </div>
          </forgotPassword.Form>
          <Link
            to="/auth/login"
            className="mt-11 text-center text-body-sm font-bold"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
